/**
 * The organization wizard's own front door (registration-restructure §14.3,
 * registration-wallets D17/D18; found live 2026-09-14).
 *
 * A signed-in member reaches "Register Organization…" from the gear menu and
 * never passes the anonymous entry page — so the wizard itself has to take an
 * organization invitation or a voucher, and it has to do so BEFORE the Domain
 * step: an invitation may fix the domain, and the Identity step already builds
 * the domain claim. This module is the decision; the wizard's Code step is a
 * form around it, and the payment step keeps its voucher-only field for a code
 * remembered late.
 *
 * Reuses the entry page's resolvers (one scrypt + one `inspect` per code), so
 * the two doors judge a code identically. DOM-free; `org-entry-code.test.ts`
 * pins every branch.
 */
import {
    classifyEntryCode, resolveInvitationRoute, resolveVoucherOffer,
    defaultInvitationRouteDeps, defaultVoucherRouteDeps,
    type InvitationRouteDeps, type VoucherRouteDeps,
} from './register-entry-view';

export type OrgEntryCodeResult =
    | { kind: 'none' }
    /** An organization invitation; `domain` is the one it fixes, if any. */
    | { kind: 'invitation'; code: string; domain: string | null }
    /** An organization voucher, read with the node: the Payment step confirms
     *  it, the Completion step spends it (§7). */
    | { kind: 'voucher'; code: string }
    | { kind: 'refused'; message: string };

export interface OrgEntryCodeDeps {
    invitation: InvitationRouteDeps;
    voucher: VoucherRouteDeps;
}

export const defaultOrgEntryCodeDeps: OrgEntryCodeDeps = {
    invitation: defaultInvitationRouteDeps,
    voucher: defaultVoucherRouteDeps,
};

const refused = (message: string): OrgEntryCodeResult => ({ kind: 'refused', message });

const SIGN_IN_PAGE = 'Use it from the sign-in page: Register, then enter the code.';

export async function resolveOrganizationEntryCode(
    raw: string | null | undefined,
    deps: OrgEntryCodeDeps = defaultOrgEntryCodeDeps,
    now: Date = new Date(),
): Promise<OrgEntryCodeResult> {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) return { kind: 'none' };

    const entry = classifyEntryCode(trimmed);
    if (!entry) {
        return refused('That does not look like a Catalog.ID code. Invitation codes start with INV-, '
            + 'registration codes with RGV-.');
    }

    if (entry.kind === 'invitation') {
        let route;
        try {
            route = await resolveInvitationRoute(entry.code, deps.invitation);
        } catch (err) {
            // The node's answer ("expired", "revoked", "invalid") is exactly what
            // the holder of the card needs to read — the entry page's rule.
            return refused(err instanceof Error && err.message
                ? err.message
                : 'That invitation could not be read. Check the code printed on your card.');
        }
        if (route.registrationType === 'organization') {
            return { kind: 'invitation', code: entry.code, domain: route.domain };
        }
        if (route.registrationType === 'personal') {
            return refused(`This invitation is for a personal registration, not an organization. ${SIGN_IN_PAGE}`);
        }
        return refused('This is a delegate invitation — a seat in an existing organization, not a new one. '
            + 'Open it from the sign-in page (Register) or from the link on its card.');
    }

    let offer;
    try {
        ({ offer } = await resolveVoucherOffer(entry.code, deps.voucher, now));
    } catch {
        // Malformed and unknown are the SAME 401 on the node (§5, no oracle).
        return refused('We do not recognise that registration code. Check it against the one '
            + 'printed on your voucher.');
    }
    if (offer.kind === 'unusable') return refused(offer.message);
    if (offer.kind === 'personalize-only') {
        return refused('That code is a delegate seat, not an organization registration. An organization '
            + 'spends it from its Delegates pane; it cannot found a new organization.');
    }
    if (offer.registrationType !== 'organization') {
        return refused(`That code pays for a personal registration, not an organization. ${SIGN_IN_PAGE}`);
    }
    return { kind: 'voucher', code: entry.code };
}

/**
 * Carry the decision into the wizard's shared state — the same three keys the
 * entry-page launch options set (`invitationCode`, `invitationDomain`,
 * `checkedVoucherCode`), so every later step behaves as if the code had arrived
 * from the entry page. A fixed domain is ADOPTED into `domain`: the buyer paid
 * for it, and a founder who typed another would be refused at begin with
 * nothing gained. A refusal changes nothing; "no code" clears both kinds, so
 * going Back and emptying the box really removes a code.
 */
export function applyOrganizationEntryCode(shared: Record<string, unknown>, result: OrgEntryCodeResult): void {
    if (result.kind === 'refused') return;
    delete shared['invitationCode'];
    delete shared['invitationDomain'];
    delete shared['checkedVoucherCode'];
    if (result.kind === 'invitation') {
        shared['invitationCode'] = result.code;
        if (result.domain) {
            shared['invitationDomain'] = result.domain;
            shared['domain'] = result.domain;
        }
    } else if (result.kind === 'voucher') {
        shared['checkedVoucherCode'] = result.code;
    }
}
